import * as React from 'react'
import { FixedSizeList as List } from 'react-window'
import type { ListOnItemsRenderedProps } from 'react-window'
import { m, useMotionValue, animate, LazyMotion, domAnimation } from 'framer-motion'
import { tw } from 'utils/classnames'
import { ScrollShadow } from '../scroll-shadow'

import { CarouselItem } from './components/carousel-item'
import type { CarouselItemData } from './components/carousel-item'
import { ScrollButton } from './components/scroll-button'
import type { ButtonSize, ButtonVariant } from '@repo/studio-design-system-ui-button'

/**
 * Number of times to repeat the item list when looping is active.
 * This creates a virtual list large enough that users will never reach the edge.
 */
const LOOP_REPEAT_FACTOR = 100
const LIST_STYLE = { scrollbarWidth: 'none' } as const

interface LoopingCarouselProps<T> {
  items: T[]
  ItemComponent: React.ComponentType<{ item: T; realIndex: number }>
  itemWidth: number
  gap?: number
  hasNextPage: boolean
  isFetchingNextPage: boolean
  fetchNextPage: () => void
  className?: string
  /**
   * The button height used to position the scroll buttons.
   * This may be different from the item height, in order to align the buttons with something specific from the item.
   */
  buttonHeight?: number
  height?: number
  shadowColor?: string
  buttonVariant?: ButtonVariant
  buttonSize?: ButtonSize
  renderScrollButton?: (props: ScrollButtonRenderProps) => React.ReactNode
}

export interface ResponsiveLoopingCarouselLayout {
  className: string
  itemWidth: number
  height: number
  buttonHeight?: number
  buttonSize?: ButtonSize
}

interface ResponsiveLoopingCarouselProps<T> {
  items: T[]
  ItemComponent: React.ComponentType<{ item: T; realIndex: number }>
  layouts: readonly ResponsiveLoopingCarouselLayout[]
  gap?: number
  hasNextPage: boolean
  isFetchingNextPage: boolean
  fetchNextPage: () => void
}

export function ResponsiveLoopingCarousel<T>({
  items,
  ItemComponent,
  layouts,
  gap,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: ResponsiveLoopingCarouselProps<T>) {
  return (
    <>
      {layouts.map(layout => (
        <LoopingCarousel
          key={layout.className}
          items={items}
          ItemComponent={ItemComponent}
          gap={gap}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          {...layout}
        />
      ))}
    </>
  )
}

export interface ScrollButtonRenderProps {
  variant: 'left' | 'right'
  buttonHeight: number
  onClick: () => void
}

export function LoopingCarousel<T>({
  items,
  ItemComponent,
  itemWidth,
  gap = 8,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  className,
  buttonHeight = 200,
  height = 300,
  shadowColor = 'text-gray-800',
  buttonVariant,
  buttonSize,
  renderScrollButton,
}: LoopingCarouselProps<T>) {
  const listRef = React.useRef<List>(null)
  const outerRef = React.useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = React.useState(0)

  const lastScrollOffsetRef = React.useRef(0)
  const loopInitializedRef = React.useRef(false)
  const resizeObserverRef = React.useRef<ResizeObserver | null>(null)

  const mouseXRef = React.useRef(-1)
  const hoverSideRef = React.useRef<'left' | 'right' | null>(null)
  const canScrollLeftRef = React.useRef(false)

  // Framer motion values — updated imperatively, no re-renders.
  const leftShadowOpacity = useMotionValue(0)
  const leftButtonOpacity = useMotionValue(0)
  const rightButtonOpacity = useMotionValue(0)

  const isLooping = !hasNextPage && items.length > 0
  const totalItemWidth = itemWidth + gap
  const virtualItemCount = isLooping ? items.length * LOOP_REPEAT_FACTOR : items.length

  if (!isLooping) {
    loopInitializedRef.current = false
  }

  const containerRefCallback = React.useCallback((node: HTMLDivElement | null) => {
    resizeObserverRef.current?.disconnect()

    if (node) {
      setContainerWidth(node.offsetWidth)
      const observer = new ResizeObserver(() => {
        setContainerWidth(node.offsetWidth)
      })
      observer.observe(node)
      resizeObserverRef.current = observer
    }
  }, [])

  // --- Stable callbacks for react-window ---
  // FixedSizeList extends PureComponent. New function references for onScroll,
  // onItemsRendered, or children cause a full List re-render (including the
  // inner container div), which causes a visual flicker.
  //
  // We keep the latest handler logic in a ref and create a stable wrapper
  // that delegates to it. The wrapper never changes identity.

  function handleItemsRendered({ visibleStopIndex }: ListOnItemsRenderedProps) {
    if (isLooping && !loopInitializedRef.current) {
      loopInitializedRef.current = true
      const middleIndex = Math.floor(virtualItemCount / 2)
      const alignedIndex = middleIndex - (middleIndex % items.length)
      listRef.current?.scrollToItem(alignedIndex, 'start')
    }

    if (hasNextPage && !isFetchingNextPage) {
      const visibleCount = Math.ceil(containerWidth / totalItemWidth)
      const remainingItems = items.length - visibleStopIndex
      if (remainingItems < visibleCount) {
        fetchNextPage()
      }
    }
  }

  function handleScroll(scrollData: { scrollOffset: number; scrollUpdateWasRequested: boolean }) {
    // react-window bug workaround: when items change it may reset scroll to 0.
    if (scrollData.scrollOffset === 0 && scrollData.scrollUpdateWasRequested) {
      listRef.current?.scrollTo(lastScrollOffsetRef.current)
      return
    }
    lastScrollOffsetRef.current = scrollData.scrollOffset
    const canScroll = scrollData.scrollOffset > 0
    if (canScroll !== canScrollLeftRef.current) {
      canScrollLeftRef.current = canScroll
      updateOverlays()
    }
  }

  const handleScrollRef = React.useRef(handleScroll)
  handleScrollRef.current = handleScroll

  const handleItemsRenderedRef = React.useRef(handleItemsRendered)
  handleItemsRenderedRef.current = handleItemsRendered

  // Stable wrappers — created once, delegates to latest handler via ref.
  const stableOnScroll = React.useMemo(
    () => (data: { scrollOffset: number; scrollUpdateWasRequested: boolean }) =>
      handleScrollRef.current(data),
    [],
  )
  const stableOnItemsRendered = React.useMemo(
    () => (props: ListOnItemsRenderedProps) => handleItemsRenderedRef.current(props),
    [],
  )

  const itemData = React.useMemo<CarouselItemData>(
    () => ({
      items: items as unknown[],
      ItemComponent: ItemComponent as React.ComponentType<{ item: unknown; realIndex: number }>,
      isLooping,
      itemWidth,
      gap,
    }),
    [items, ItemComponent, isLooping, itemWidth, gap],
  )

  function scrollBy(direction: 'left' | 'right') {
    if (!outerRef.current) {
      return
    }
    // Scroll by whole items (including gap) that fill one viewport width
    const itemsPerPage = Math.max(1, Math.floor(containerWidth / totalItemWidth))
    const delta = itemsPerPage * totalItemWidth
    const offset = lastScrollOffsetRef.current
    const target = direction === 'left' ? Math.max(0, offset - delta) : offset + delta

    outerRef.current.scrollTo({ left: target, behavior: 'smooth' })
  }

  function updateOverlays() {
    const hover = hoverSideRef.current
    const canScroll = canScrollLeftRef.current

    animate(leftShadowOpacity, canScroll ? 1 : 0)
    animate(leftButtonOpacity, hover === 'left' && canScroll ? 1 : 0)
    animate(rightButtonOpacity, hover === 'right' ? 1 : 0)
  }

  function handleMouseMove(e: React.MouseEvent<HTMLDivElement>) {
    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - rect.left
    mouseXRef.current = x
    const side = x < containerWidth / 2 ? 'left' : 'right'
    if (side !== hoverSideRef.current) {
      hoverSideRef.current = side
      updateOverlays()
    }
  }

  function handleMouseLeave() {
    mouseXRef.current = -1
    hoverSideRef.current = null
    updateOverlays()
  }

  if (items.length === 0) {
    return null
  }

  return (
    <LazyMotion features={domAnimation}>
      <div
        ref={containerRefCallback}
        className={tw('relative', className)}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {containerWidth > 0 ? (
          <List
            className="!overflow-y-hidden"
            style={LIST_STYLE}
            height={height}
            itemCount={virtualItemCount}
            itemSize={totalItemWidth}
            width={containerWidth}
            layout="horizontal"
            ref={listRef}
            outerRef={outerRef}
            itemData={itemData}
            onItemsRendered={stableOnItemsRendered}
            onScroll={stableOnScroll}
          >
            {CarouselItem}
          </List>
        ) : null}

        <ScrollShadow.Root className="absolute inset-0 size-full pointer-events-none z-[0]">
          <m.div style={{ opacity: leftShadowOpacity }}>
            <ScrollShadow.Shadow position="left" className={tw(shadowColor, 'opacity-100')} />
          </m.div>
          <ScrollShadow.Shadow
            position="right"
            className={tw(shadowColor, 'translate-x-px opacity-100')}
          />
        </ScrollShadow.Root>

        <div className="absolute inset-0 size-full hidden @5xl:block pointer-events-none">
          <m.div style={{ opacity: leftButtonOpacity }}>
            {renderScrollButton ? (
              renderScrollButton({
                variant: 'left',
                buttonHeight,
                onClick: () => scrollBy('left'),
              })
            ) : (
              <ScrollButton
                variant="left"
                buttonHeight={buttonHeight}
                buttonVariant={buttonVariant}
                buttonSize={buttonSize}
                onClick={() => scrollBy('left')}
              />
            )}
          </m.div>
          <m.div style={{ opacity: rightButtonOpacity }}>
            {renderScrollButton ? (
              renderScrollButton({
                variant: 'right',
                buttonHeight,
                onClick: () => scrollBy('right'),
              })
            ) : (
              <ScrollButton
                variant="right"
                buttonHeight={buttonHeight}
                buttonVariant={buttonVariant}
                buttonSize={buttonSize}
                onClick={() => scrollBy('right')}
              />
            )}
          </m.div>
        </div>
      </div>
    </LazyMotion>
  )
}
